// The verdict slot, standing empty.
//
// This is the sharpest thing on the screen, and it is a CARD with a full 2px
// warn border on all four sides — not an `.alert`, which is a left-stripe note.
// The difference is the point: a note is something said beside a result, and
// this is the result's own place, occupied by the reason there is no result. It
// carries no pill and no icon (there is no state to badge), it is not
// dismissible (nobody may dismiss the absence of a decision), and it renders
// ABOVE the tally.
//
// **The tallies stay on screen unchanged beneath it.** The card withholds the
// DECISION, not the arithmetic — every number a person needs in order to settle
// this is still on the page (motion.ts:171: "withholding the decision is not
// withholding the arithmetic").
//
// Two variants, because there are two ways a decision is withheld:
//   • a motion whose roll and vote set disagree (`carried === null`,
//     `votes-exceed-roll`); and
//   • an election with a tie at a cut line and no recorded resolution
//     (`outcome: 'tie-unresolved'`, SO 15.13).
//
// `outcome: 'shortfall'` is NOT this card. It is `status: 'ok'` and a lawful
// result — the places go to Standing Committee under SO 15.12 — so it gets an
// `.alert` in PositionResult, not the verdict slot standing empty.
//
// REGISTRAR'S WORDING, and it is marked as such where it lands: the paragraph
// beginning "Nothing needs to be said to Synod from this screen" suggests a form
// of words for the President. Words said at Synod are not a builder's to write;
// this text is a placeholder awaiting the registrar's ruling. It is now the ONLY
// such placeholder in the package — the other was the Bishop sentence, and the
// component that carried it no longer exists.
//
// ONE READING, and it is a scrutineer's. This card said the same things twice
// for a day — once with the figures and once without, for a member — and the
// registrar's ruling of 2026-08-16 removed the second reader rather than the
// second copy: no member sees this page at all. So there is no branch here, no
// `viewer` prop, and no "what may this reader see" question inside the card. The
// question is asked once, about the whole page, and it is not asked in code —
// see the banner at the top of ResultPage.tsx.

import type { JSX } from 'react';
import type {
  Contender,
  House,
  MajorityBase,
  MotionItemResult,
  PositionItemResult,
  TieReport,
} from '@cca/decides-count';
import {
  SCOPE_COLUMN_LABEL,
  plural,
  refusedScopes,
  scopePhrase,
  votesPhrase,
  withheldScopes,
} from './view';

/** The roll that the papers were counted against, named as a reader would name
 *  it. Since cca-vr7u a `present-and-voting` motion DOES reach this card: its
 *  papers are measured against `rollForScope` (the persons present and voting,
 *  bishop-aware), and `votes-exceed-roll` is raised when `cast > rollForScope`.
 *  The map is total over `MajorityBase` besides. */
const ROLL_NAME: Record<MajorityBase, string> = {
  'present-and-voting': 'present roll',
  'summoned-per-house': 'summons roll',
  'all-synod-members': 'standing roll of Synod',
};

/** The same roll, as a column heading and as the noun in "a roll of 60 …". */
const ROLL_NOUN: Record<MajorityBase, { heading: string; inline: string }> = {
  'present-and-voting': { heading: 'Members present', inline: 'members present' },
  'summoned-per-house': { heading: 'Members summoned', inline: 'members summoned' },
  'all-synod-members': { heading: 'Members of Synod', inline: 'members of Synod' },
};

/** "the clergy figures" / "the lay figures". */
const SCOPE_ADJECTIVE: Record<House | 'all', string> = {
  clergy: 'clergy',
  laity: 'lay',
  all: 'Synod',
};

interface MotionWithheldProps {
  result: MotionItemResult;
}

/** A motion whose roll snapshot and vote set disagree. */
export function MotionWithheldCard({ result }: MotionWithheldProps): JSX.Element {
  const scopes = withheldScopes(result);
  const refused = refusedScopes(result);
  const rollNoun = ROLL_NOUN[result.base];
  const scopeList = scopes.map((scope) => scopePhrase(scope.scope)).join(' and ');

  return (
    <section className="card result-withheld" aria-labelledby={`withheld-${result.itemId}`}>
      <h3 className="result-withheld-title" id={`withheld-${result.itemId}`}>
        No decision is reported for this motion.
      </h3>

      {scopes.map((scope) => (
        <p className="result-withheld-text" key={scope.scope}>
          In {scopePhrase(scope.scope)}, {plural(scope.cast, 'paper', 'papers')}{' '}
          {scope.cast === 1 ? 'was' : 'were'} counted against a roll of {String(scope.rollForScope)}{' '}
          {rollNoun.inline}. There are more papers than people, so the roll and the votes disagree.
        </p>
      ))}

      <p className="result-withheld-text">
        <strong>
          This is not a result. The motion has not been carried and it has not been lost.
        </strong>{' '}
        Nothing has been declared, and nothing here may be declared until the disagreement is
        settled.
      </p>

      {/* Only when a SIBLING scope definitively refused the motion. The engine
          propagates `null` strictly and deliberately does NOT apply Kleene's
          `false ∧ unknown = false` (motion.ts:194-205): the two scopes are not
          independent, because both tallies came out of the same papers. This
          paragraph is that reasoning in words — without it, a reader looking at
          a "Not carried" beside the clergy column concludes the motion is dead. */}
      {refused.map((scope) => (
        <p className="result-withheld-text" key={scope.scope}>
          {sentenceCase(votesPhrase(scope))}, which on its own would refuse the motion. That does
          not settle it. Both tallies came from the same papers, so if a paper has been counted
          twice, the {SCOPE_ADJECTIVE[scope.scope]} figures are in doubt as well.
        </p>
      ))}

      <p className="result-withheld-text">
        Two things can be wrong, and they are checked separately:
      </p>
      <ul className="result-withheld-list">
        <li>
          the {ROLL_NAME[result.base]} for {scopeList} may be short — a name missing from it, or a
          member not recorded;
        </li>
        <li>the papers may contain a duplicate — a re-imported file, or a paper counted twice.</li>
      </ul>

      <p className="result-withheld-text">
        When the registrar has settled which of the two is wrong, run the count again. A new count
        is recorded and this one is kept, so the sequence stays readable to a scrutineer.
      </p>

      {/* MEASURES DOWN THE SIDE, HOUSES ACROSS THE TOP — the same shape as the
          tally itself, and not the shape this started as.
          "Papers counted | Members summoned | Difference" as COLUMN headings put
          three long labels on one row above three short numbers: at 320px their
          min-content width alone exceeded the card, which is the SC 1.4.10 fault
          the House tally had. Turned on its side, the long words are row labels
          that wrap freely and each column is as wide as a two-digit number. Every
          figure the design asked for is still here, and `th scope="row"` per
          measure is the more accurate association besides. */}
      <table className="table result-table">
        <caption className="result-table-caption">The two numbers that disagree.</caption>
        <thead>
          <tr>
            <th scope="col">
              <span className="ballot-sr">What was counted</span>
            </th>
            {scopes.map((scope) => (
              <th scope="col" className="num" key={scope.scope}>
                {SCOPE_COLUMN_LABEL[scope.scope]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row">Papers counted</th>
            {scopes.map((scope) => (
              <td className="num" key={scope.scope}>
                {scope.cast}
              </td>
            ))}
          </tr>
          <tr>
            <th scope="row">{rollNoun.heading}</th>
            {scopes.map((scope) => (
              <td className="num" key={scope.scope}>
                {scope.rollForScope}
              </td>
            ))}
          </tr>
          {/* NOT "Difference", and not a signed integer (ruling, 2026-08-16).
              The measure is `cast - rollForScope`, and it used to read "+1" here while
              the tally's "Did not vote" cell read "−1" — one fact stated twice,
              once with the sign inverted, and a reader scanning a minus reads
              FEWER when the truth is MORE. The words now carry the direction and
              the cell carries a plain count, so the row reads "more papers than
              people: 1" both by eye and to a screen reader through
              `th scope="row"`.
              Unsigned safely, and not by hoping: this card renders only for
              scopes the ENGINE withheld, and it withholds exactly when the
              papers outnumber the roll (`votes-exceed-roll`), so the subtraction
              here is always positive. It is not re-derived from the sign of the
              figure — see HouseTally, same rule. */}
          <tr>
            <th scope="row">More papers than people</th>
            {scopes.map((scope) => (
              <td className="num" key={scope.scope}>
                {scope.cast - scope.rollForScope}
              </td>
            ))}
          </tr>
        </tbody>
      </table>

      {/* REGISTRAR'S WORDING REQUIRED — placeholder, pending their ruling. It is
          advice to the President about what to say in the room, which is where
          the outcome is declared and the only place it is (ruling, 2026-08-16).
          This screen is what the scrutineers check it against. */}
      <p className="result-withheld-text">
        Nothing needs to be said to Synod from this screen. If a form of words is wanted, the
        President may say that the count for this item is not complete and the result will be
        declared later in this sitting.
      </p>

      <HelpLine />
    </section>
  );
}

interface TieWithheldProps {
  result: PositionItemResult;
  tie: TieReport;
  candidateNames: Record<string, string>;
  /** True when every place this ballot has NOT filled is accounted for by this
   *  tie — i.e. nothing else on the item is unsettled. */
  everythingElseSettled: boolean;
}

/**
 * A tie at a cut line with no recorded resolution (SO 15.13).
 *
 * Two sub-variants, and the difference is not cosmetic. At the ELECTION cut
 * nobody has taken the place. At the ALTERNATE cut everyone in the tied set is
 * certainly elected — what is unsettled is which of them is the alternate (SO
 * 15.10, position.ts:296-320; PositionItemResult.substantive: "That is the
 * honest report, not a defect"). Saying "no one is declared elected" there would
 * be false in the direction that unseats someone.
 */
export function TieWithheldCard({
  result,
  tie,
  candidateNames,
  everythingElseSettled,
}: TieWithheldProps): JSX.Element {
  const alternate = tie.at === 'alternate';
  const who = tie.contenders.map((contender) => contenderName(contender, candidateNames));
  const whoList = joinAnd(who);
  const headingId = `tie-${result.itemId}-${tie.at}`;

  return (
    <section className="card result-withheld" aria-labelledby={headingId}>
      <h3 className="result-withheld-title" id={headingId}>
        {alternate
          ? 'Which of them is the alternate is not settled.'
          : `No one is declared elected to ${tie.seatsAvailable === 1 ? 'this place' : 'these places'} yet.`}
      </h3>

      <p className="result-withheld-text">
        {whoList} are level on {tie.votes} votes
        {alternate ? '' : ` with ${plural(tie.seatsAvailable, 'place', 'places')} left`}.{' '}
        {alternate
          ? 'Both hold a place — that much is settled — but Standing Order 15.10 makes the alternate the lowest-polling of those elected, and the votes do not settle which of them that is.'
          : 'The votes do not settle which of them takes it.'}
      </p>

      <p className="result-withheld-text">
        Standing Order 15.13 says what happens next: a fresh vote among them, and if that is level
        as well, the Registrar draws by lot with an equal chance.{' '}
        <strong>There is no casting vote.</strong> Nothing may be declared for{' '}
        {alternate ? 'the alternate place' : 'this place'} until one of those has been held.
      </p>

      {everythingElseSettled ? (
        <p className="result-withheld-text">
          Every other place on this ballot is settled and is shown above.
        </p>
      ) : null}

      <p className="result-withheld-text">
        When the fresh vote or the draw has been held, record it and run the count again.
      </p>

      <HelpLine />
    </section>
  );
}

function HelpLine(): JSX.Element {
  return (
    <p className="result-withheld-help">
      Having any problems? Email{' '}
      <a href="mailto:registrar@confessinganglicans.nz">registrar@confessinganglicans.nz</a>
    </p>
  );
}

/** No Confidence is a CONTENDER, not a spoiled paper (SO 15.7), so it can be one
 *  of the tied and is named as itself. */
function contenderName(contender: Contender, names: Record<string, string>): string {
  if (contender.kind === 'no-confidence') return 'No Confidence';
  return names[contender.candidateId] ?? contender.candidateId;
}

function joinAnd(parts: string[]): string {
  const last = parts.at(-1);
  if (last === undefined) return '';
  if (parts.length === 1) return last;
  return `${parts.slice(0, -1).join(', ')} and ${last}`;
}

function sentenceCase(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
